import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import useSWR from 'swr';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import Checkbox from '@/components/@IntranetLibrary/Checkbox';
import MobileBottomSheet from '@/components/@IntranetLibrary/MobileBottomSheet';
import Modal, { type ModalRef } from '@/components/@IntranetLibrary/Modal';
import { RadioGroupV2 } from '@/components/@IntranetLibrary/Radio/RadioV2';
import Spinner from '@/components/@IntranetLibrary/Spinner';
import { PLACEHOLDER_COLOR } from '@/components/@IntranetLibrary/DatePicker/styles';
import { approvalOutlineSelectSx } from '@/components/Approval/@components/ApprovalFormTable';
import { InfoSectionTitle } from '@/components/Approval/@components/InfoSection';
import {
  buildPlmCardHtml,
  buildPlmContentHtml,
  PlmCardActions,
  ProductLifecycleRoot,
  ProductLifecycleSnapshot,
} from '@/components/Approval/@components/ProductLifecycle';
import SearchableSelect, {
  type SearchableSelectOption,
} from '@/components/Approval/@components/SearchableSelect';
import { getProductLifecycleDraftSourceUrl } from '@/constant/approval';
import type { ReplaceContentApi } from '@/components/LexicalEditor/plugins/ReplaceContentPlugin';
import { useSnackbar } from '@/hooks/global-tools';
import useLayoutSize from '@/hooks/useLayoutSize';
import { getNestedDataFetcher } from '@/utils/apis';
import {
  buildPlmEditorContent,
  buildPlmStyleNumber,
  buildPlmSubject,
  PLM_PLACEHOLDERS,
  PLM_TYPE_OPTIONS,
  plmCandidateKey,
  type PlmCard,
  type PlmDraftSource,
  type PlmItem,
  type PlmType,
} from '@/utils/approval/productLifecycle';
import { FieldBlock, FieldHeader } from './styles';
import type { ApprovalFormValues } from './types';

const CONTROL_HEIGHT = 44;

type Props = { isEditMode: boolean; editorApi: ReplaceContentApi | null };

/** 「추가 품번 확인」 — 지금까지 고른 품번. PC 모달, 모바일 바텀시트 */
const StyleNoDialog = ({
  open,
  onClose,
  styleNos,
}: {
  open: boolean;
  onClose: () => void;
  styleNos: string[];
}) => {
  const { t } = useTranslation();
  const { isMobile } = useLayoutSize();
  const modalRef = useRef<ModalRef>(null);
  const title = t('추가 품번 확인');

  useEffect(() => {
    if (isMobile) return;
    if (open) modalRef.current?.open();
    else modalRef.current?.close();
  }, [open, isMobile]);

  const body = (
    <ProductLifecycleRoot>
      {styleNos.length > 0 && (
        <table>
          <tbody>
            {styleNos.map((no, i) => (
              <tr key={`${no}-${i}`}>
                <td>{no}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </ProductLifecycleRoot>
  );

  if (isMobile)
    return (
      <MobileBottomSheet
        open={open}
        onClose={onClose}
        title={title}
        height="auto"
        maxHeight="80vh"
        swipeable={false}
      >
        <SheetBody>{body}</SheetBody>
      </MobileBottomSheet>
    );
  return (
    <Modal ref={modalRef} title={title} handleClose={onClose}>
      {body}
    </Modal>
  );
};

/**
 * GERP PLM(123) — PLM 시스템을 고르고 내 미상신 품번을 여러 건 체크하면 품번마다 카드를 불러옴.
 * 고르거나 뺄 때마다 제목·본문을 덮어씀. 편집은 품번을 바꿀 수 없고 저장 카드만
 */
const ProductLifecycleField = ({ isEditMode, editorApi }: Props) => {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const { control, setValue } = useFormContext<ApprovalFormValues>();
  const saved = useWatch({ control, name: 'productLifecycle' });

  const [plmType, setPlmType] = useState<PlmType | null>(null);
  /** 고른 순서 그대로 */
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [cards, setCards] = useState<Record<string, PlmCard>>({});
  const [loadingKeys, setLoadingKeys] = useState<string[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  // 카드 응답(비동기)이 최신 선택·카드를 보도록 state 와 같이 둠
  const selectedRef = useRef<string[]>([]);
  const cardsRef = useRef<Record<string, PlmCard>>({});
  // PLM 시스템을 바꾼 뒤 늦게 온 이전 카드 응답은 버림
  const generationRef = useRef(0);

  const { data: source, isLoading } = useSWR<PlmDraftSource>(
    isEditMode || !plmType ? null : getProductLifecycleDraftSourceUrl(plmType),
    getNestedDataFetcher,
    {
      revalidateOnFocus: false,
      onError: () =>
        snackbar.showError({ message: t('데이터를 불러오지 못했습니다.') }),
    },
  );

  const candidateByKey = useMemo(() => {
    const map = new Map<
      string,
      NonNullable<PlmDraftSource['display']['candidates']>[number]
    >();
    if (!plmType) return map;
    (source?.display.candidates ?? []).forEach((c) => {
      const key = plmCandidateKey(c, plmType);
      if (key) map.set(key, c);
    });
    return map;
  }, [source, plmType]);

  const toItems = (keys: string[], loaded: Record<string, PlmCard>) =>
    keys.flatMap<PlmItem>((key) => {
      const c = candidateByKey.get(key);
      if (!c) return [];
      return [
        {
          key,
          styleNo: c.styleNo ?? '',
          styleName: c.styleName ?? '',
          styleNumber: buildPlmStyleNumber(c),
          card: loaded[key] ?? {},
        },
      ];
    });

  /** 카드를 다 받았을 때만 저장 값 — 덜 받았으면 상신 시 안내 */
  const syncSaved = (
    type: PlmType,
    keys: string[],
    loaded: Record<string, PlmCard>,
  ) => {
    const items = toItems(keys, loaded);
    const isReady =
      items.length > 0 &&
      items.length === keys.length &&
      keys.every((key) => loaded[key]);
    setValue(
      'productLifecycle',
      isReady
        ? {
            plmType: type,
            styleNumber: items.map((item) => item.styleNumber).join(','),
            content: buildPlmContentHtml(items, type, t),
          }
        : null,
      { shouldDirty: true },
    );
  };

  /** 레거시 change_title_content — 고르거나 뺄 때마다 제목·본문 덮어씀 */
  const overwriteSubjectAndBody = (keys: string[]) => {
    const items = toItems(keys, cardsRef.current);
    setValue('subject', buildPlmSubject(items, t), { shouldDirty: true });
    editorApi?.replace(buildPlmEditorContent(items, t));
  };

  const update = (keys: string[], loaded: Record<string, PlmCard>) => {
    selectedRef.current = keys;
    cardsRef.current = loaded;
    setSelectedKeys(keys);
    setCards(loaded);
  };

  const loadCard = async (type: PlmType, key: string) => {
    const generation = generationRef.current;
    setLoadingKeys((prev) => [...prev, key]);
    try {
      const data = await getNestedDataFetcher<PlmDraftSource>(
        getProductLifecycleDraftSourceUrl(type, [key]),
      );
      if (generation !== generationRef.current) return;
      const card = data.display.content?.cards?.[0];
      if (!card) throw new Error('card missing');
      const next = { ...cardsRef.current, [key]: card };
      update(selectedRef.current, next);
      syncSaved(type, selectedRef.current, next);
    } catch {
      if (generation !== generationRef.current) return;
      // 같은 품번을 다시 골라 재시도할 수 있게 선택에서 뺌
      const next = selectedRef.current.filter((k) => k !== key);
      update(next, cardsRef.current);
      overwriteSubjectAndBody(next);
      syncSaved(type, next, cardsRef.current);
      snackbar.showError({ message: t('데이터를 불러오지 못했습니다.') });
    } finally {
      if (generation === generationRef.current)
        setLoadingKeys((prev) => prev.filter((k) => k !== key));
    }
  };

  const applySelection = (nextKeys: string[]) => {
    if (!plmType) return;
    const added = nextKeys.filter((key) => !selectedRef.current.includes(key));
    update(nextKeys, cardsRef.current);
    overwriteSubjectAndBody(nextKeys);
    syncSaved(plmType, nextKeys, cardsRef.current);
    added.forEach((key) => loadCard(plmType, key));
  };

  /** 레거시: PLM 시스템을 바꾸면 고른 품번·카드·제목·본문 모두 지움 */
  const handlePlmType = (next: PlmType) => {
    if (next === plmType) return;
    generationRef.current += 1;
    setPlmType(next);
    update([], {});
    setLoadingKeys([]);
    setValue('subject', '', { shouldDirty: true });
    editorApi?.replace('');
    setValue('productLifecycle', null, { shouldDirty: true });
  };

  if (isEditMode)
    return (
      <FieldBlock>
        {saved?.content && <ProductLifecycleSnapshot content={saved.content} />}
      </FieldBlock>
    );

  const candidates = source?.display.candidates ?? [];
  const options: SearchableSelectOption[] = plmType
    ? candidates.map((c) => {
        const key = plmCandidateKey(c, plmType);
        const label = c.title ?? c.styleNo ?? key;
        return {
          value: key,
          label,
          labelNode: (
            <OptionLabel>
              <Checkbox
                size="small"
                checked={selectedKeys.includes(key)}
                tabIndex={-1}
                sx={{ p: 0, pointerEvents: 'none' }}
              />
              {label}
            </OptionLabel>
          ),
        };
      })
    : [];
  const placeholder = plmType ? t(PLM_PLACEHOLDERS[plmType]) : '';
  const items = toItems(selectedKeys, cards);

  return (
    <FieldBlock>
      <FieldHeader>
        <InfoSectionTitle>{t('PLM 시스템')}</InfoSectionTitle>
      </FieldHeader>
      <RadioGroupV2
        row
        value={plmType ?? ''}
        onChange={(e) => handlePlmType(e.target.value as PlmType)}
        options={PLM_TYPE_OPTIONS}
        sx={{ gap: '8px', mb: 1 }}
      />
      {plmType && (
        <ControlRow>
          <SearchableSelect
            multiple
            value={selectedKeys}
            options={options}
            placeholder={isLoading ? t('불러오는 중...') : placeholder}
            noOptionsText={isLoading ? null : t('목록이 없습니다.')}
            // 레거시 콤보는 고른 품번 대신 안내 문구를 둠 — 고른 품번은 「추가 품번 확인」
            renderValue={() => <Placeholder>{placeholder}</Placeholder>}
            onChange={applySelection}
            sx={{
              ...approvalOutlineSelectSx,
              flex: 1,
              minWidth: 0,
              height: CONTROL_HEIGHT,
            }}
          />
          <BoxButton
            type="button"
            variant="secondary"
            size="medium"
            onClick={() => setIsDialogOpen(true)}
          >
            {t('추가 품번 확인')}
          </BoxButton>
          {loadingKeys.length > 0 && <Spinner theme="black" size="16px" />}
        </ControlRow>
      )}
      {plmType &&
        items
          .filter((item) => cards[item.key])
          .map((item) => (
            <CardBlock key={item.key}>
              <PlmCardActions>
                <BoxButton
                  type="button"
                  variant="secondary"
                  size="small"
                  onClick={() =>
                    applySelection(selectedKeys.filter((k) => k !== item.key))
                  }
                >
                  {t('삭제')}
                </BoxButton>
              </PlmCardActions>
              <ProductLifecycleSnapshot
                content={buildPlmCardHtml(item, plmType, t)}
                allowFileDownload
              />
            </CardBlock>
          ))}
      <StyleNoDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        styleNos={items.map((item) => item.styleNo)}
      />
    </FieldBlock>
  );
};

const ControlRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 8,
});

const OptionLabel = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
});

const Placeholder = styled('span')({ color: PLACEHOLDER_COLOR });

const CardBlock = styled(Box)({
  '& + &': { marginTop: 16 },
});

const SheetBody = styled(Box)({
  padding: '16px 16px 24px',
});

export default ProductLifecycleField;
